import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@awesome.me/kit-f3e20eab48/icons/classic/solid";
import { HOLY_WHEEL_PRIZES } from "../../../../../logic/helpers/holyWheelPrizes";
import { holyWheelTitle } from "../../../../../logic/helpers/holyWheelTitle";
import { useWheelBackNavigation } from "../../../../../logic/hooks/useWheelBackNavigation";
import sky from "../../../../../assets/static/images/holy-wheel/holy-wheel-main-bg.webp";
import clouds from "../../../../../assets/static/images/holy-wheel/holy-wheel-cloud-1.webp";
import frontClouds from "../../../../../assets/static/images/holy-wheel/holy-wheel-cloud-2.webp";
import pointerBack from "../../../../../assets/static/images/holy-wheel/wheel-pointer-back.png";
import pointerFront from "../../../../../assets/static/images/holy-wheel/wheel-pointer-front.png";
import Centre from "../../../../../assets/static/images/holy-wheel/wheel-centre.svg?react";
import Wing from "../../../../../assets/static/images/holy-wheel/wing-fallback.svg?react";
import Button from "../../../../../assets/static/images/holy-wheel/spin-button.svg?react";
import { HolyWheelDisc } from "../HolyWheelDisc/HolyWheelDisc";
import "./HolyWheelLoading.scss";

interface HolyWheelLoadingProps {
    status?: "loading" | "error" | "unavailable";
    onRetry?: () => Promise<unknown>;
}

export function HolyWheelLoading({
    status = "loading",
    onRetry,
}: HolyWheelLoadingProps) {
    const { t, i18n } = useTranslation();
    const goBack = useWheelBackNavigation();
    const [canClose, setCanClose] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);
    const isLoading = status === "loading" || isRetrying;

    useEffect(() => {
        const timeout = window.setTimeout(() => setCanClose(true), 4000);
        return () => window.clearTimeout(timeout);
    }, []);

    return (
        <main className="holy-wheel-loading" aria-label={t("holyWheel.title")}>
            {/* Decorative placeholders only: no wallet, controller or checkout. */}
            <div className="holy-wheel-loading__scene" aria-hidden="true" inert>
                <img
                    className="holy-wheel-loading__sky"
                    src={sky}
                    alt=""
                    draggable={false}
                />
                <img
                    className="holy-wheel-loading__title"
                    src={holyWheelTitle(i18n.language, i18n.resolvedLanguage)}
                    alt=""
                    draggable={false}
                />
                <div className="holy-wheel-loading__wheel">
                    <img
                        className="holy-wheel-loading__wheel-pointer"
                        src={pointerBack}
                        alt=""
                        draggable={false}
                    />
                    <HolyWheelDisc prizes={HOLY_WHEEL_PRIZES} />
                    <img
                        className="holy-wheel-loading__wheel-pointer"
                        src={pointerFront}
                        alt=""
                        draggable={false}
                    />
                    <Centre className="holy-wheel-loading__wheel-centre" />
                    <Wing className="holy-wheel-loading__wheel-wing" />
                </div>
                <Button className="holy-wheel-loading__button" />
                <img
                    className="holy-wheel-loading__clouds"
                    src={frontClouds}
                    alt=""
                    draggable={false}
                />
                <img
                    className="holy-wheel-loading__clouds"
                    src={clouds}
                    alt=""
                    draggable={false}
                />
            </div>
            <div className="holy-wheel-loading__backdrop">
                <div className="holy-wheel-loading__message">
                    <div role="status" aria-live="polite">
                        {isLoading ? (
                            <>
                                <span
                                    className="holy-wheel-loading__spinner"
                                    aria-hidden="true"
                                />
                                <span className="holy-wheel-loading__accessible-copy">
                                    {t("wheel.loading")}
                                </span>
                            </>
                        ) : (
                            <p>
                                {t(
                                    status === "error"
                                        ? "wheel.requestError"
                                        : "wheel.unavailable",
                                )}
                            </p>
                        )}
                    </div>
                    {!isLoading && onRetry && (
                        <button
                            type="button"
                            className="holy-wheel-loading__retry"
                            onClick={async () => {
                                setIsRetrying(true);
                                try {
                                    await onRetry();
                                } finally {
                                    setIsRetrying(false);
                                }
                            }}
                        >
                            {t("wheel.refresh")}
                        </button>
                    )}
                </div>
                {(canClose || status !== "loading") && (
                    <button
                        type="button"
                        className="holy-wheel-loading__close"
                        aria-label={t("holyWheel.back")}
                        onClick={goBack}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                )}
            </div>
        </main>
    );
}
